import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, X, Loader2 } from "lucide-react";

const NONE = "__none__";
const EMPTY = { company_name: "", contact_name: "", contact_email: "", contact_phone: "", website_url: "" };

export default function SupplierPicker({ value, onChange, suppliers, reload }) {
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!f.company_name.trim()) return toast.error("Company name is required");
    setSaving(true);
    try {
      const { data } = await api.post("/suppliers", f);
      await reload?.();
      onChange(data.id);
      setAdding(false);
      setF(EMPTY);
      toast.success("Supplier added");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Contact</label>
        <button type="button" data-testid="new-supplier-toggle" onClick={() => setAdding(!adding)} className="flex items-center gap-1 text-xs font-medium text-[#e84e34] hover:underline">
          {adding ? <><X className="h-3 w-3" /> Cancel</> : <><Plus className="h-3 w-3" /> New contact</>}
        </button>
      </div>
      {!adding ? (
        <Select value={value || NONE} onValueChange={(v) => onChange(v === NONE ? null : v)}>
          <SelectTrigger data-testid="supplier-select" className="rounded-none"><SelectValue placeholder="Select contact" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>No contact</SelectItem>
            {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.company_name}</SelectItem>)}
          </SelectContent>
        </Select>
      ) : (
        <div className="space-y-2 border border-zinc-200 bg-zinc-50 p-3">
          <input data-testid="supplier-company" value={f.company_name} onChange={(e) => setF({ ...f, company_name: e.target.value })} placeholder="Company name *" className="w-full border border-zinc-200 px-2 py-1.5 text-sm outline-none focus:border-[#e84e34]" />
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="supplier-contact-name" value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} placeholder="Contact name" className="border border-zinc-200 px-2 py-1.5 text-sm outline-none focus:border-[#e84e34]" />
            <input data-testid="supplier-contact-phone" value={f.contact_phone} onChange={(e) => setF({ ...f, contact_phone: e.target.value })} placeholder="Contact phone" className="border border-zinc-200 px-2 py-1.5 text-sm outline-none focus:border-[#e84e34]" />
            <input data-testid="supplier-contact-email" value={f.contact_email} onChange={(e) => setF({ ...f, contact_email: e.target.value })} placeholder="Contact email" className="border border-zinc-200 px-2 py-1.5 text-sm outline-none focus:border-[#e84e34]" />
            <input data-testid="supplier-website" value={f.website_url} onChange={(e) => setF({ ...f, website_url: e.target.value })} placeholder="Website URL" className="border border-zinc-200 px-2 py-1.5 text-sm outline-none focus:border-[#e84e34]" />
          </div>
          <button type="button" onClick={save} disabled={saving} data-testid="supplier-save" className="flex items-center gap-2 bg-zinc-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-zinc-800 disabled:opacity-60">
            {saving && <Loader2 className="h-3 w-3 animate-spin" />} Save contact
          </button>
        </div>
      )}
    </div>
  );
}
